// src/components/ui/globe/GlobalInsightsWrapper.tsx

/**
 * GlobalInsightsWrapper - Client Component
 * 
 * This component serves as a smart wrapper around the Globe visualization, handling:
 * 1. Device capability detection
 * 2. User preference monitoring (reduced motion)
 * 3. Proper component lifecycle management
 * 4. Error boundaries for graceful fallbacks
 * 
 * The wrapper uses several optimization techniques:
 * - Memoization to prevent unnecessary re-renders
 * - RequestIdleCallback for non-critical initialization
 * - Proper cleanup of event listeners
 * - Forced remounting on navigation to prevent memory leaks
 */

"use client"

import { memo, useEffect, useState, useCallback } from 'react'
import { GlobalInsights } from '~/components/ui/globe/GlobalInsights'
import { getDeviceCapabilities, INTERACTIVE_GLOBE_UNAVAILABLE_EVENT } from '~/utils/performance'
import { usePathname } from 'next/navigation'
import { StaticGlobe } from '~/components/ui/globe/GlobeStatic'
import { ErrorBoundary } from '~/components/shared/ErrorBoundary'

export const GlobalInsightsWrapper = memo(function GlobalInsightsWrapper() {
  // Track whether device can handle interactive globe
  const [shouldShowInteractive, setShouldShowInteractive] = useState(false)
  
  // Track current route for remounting purposes
  const pathname = usePathname()
  
  // Memoized handler for media query changes
  // Prevents recreation of event listener on each render
  const handleChange = useCallback(() => {
    const newCapabilities = getDeviceCapabilities()
    setShouldShowInteractive(newCapabilities.isHighPerformance)
  }, [])
  
  useEffect(() => {
    // Track mounted state to prevent updates after unmount
    let mounted = true
    // Reference to media query for cleanup
    let mediaQuery: MediaQueryList | null = null

    /**
     * Initialize device capability checking and media query listeners
     * 
     * CRITICAL DEPENDENCY MANAGEMENT:
     * 
     * REMOVED PATHNAME DEPENDENCY (Previous behavior):
     * - useEffect ran on every navigation change
     * - Caused unnecessary re-initialization of device detection
     * - Created potential memory leaks from multiple media query listeners
     * - Performance overhead from repeated capability checks
     * - Globe would remount on navigation (key={pathname} still handles this)
     * 
     * CURRENT BEHAVIOR (No pathname dependency):
     * - Device capabilities are checked ONCE on component mount
     * - Media query listener is set up ONCE and persists
     * - No unnecessary re-initialization on navigation
     * - Globe remounting is handled by key={pathname} prop instead
     * 
     * WHY THIS WORKS:
     * 1. Device capabilities don't change during a session
     * 2. Media query preferences are handled by persistent listener
     * 3. Globe remounting for navigation is handled at component level, not effect level
     * 4. Reduced effect executions = better performance
     * 
     * EDGE CASES CONSIDERED:
     * - handleChange function stability (memoized with useCallback)
     * - Media query listener cleanup on unmount (handled in cleanup)
     * - Globe state reset on navigation (handled by key={pathname} remounting)
     */
    const initializeCapabilities = (): void => {
      // Get initial device capabilities
      const capabilities = getDeviceCapabilities()
      // console.debug('Device capabilities:', capabilities)
      
      if (mounted) {
        // Set initial interactive state based on device capabilities
        setShouldShowInteractive(capabilities.isHighPerformance)
        
        // Setup media query listener for reduced motion preference
        // This ensures we respect user's accessibility preferences
        mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
        mediaQuery.addEventListener('change', handleChange)
      }
    }

    // Use requestIdleCallback for non-critical initialization
    // Falls back to immediate execution if not available
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(initializeCapabilities)
    } else {
      initializeCapabilities()
    }
    
    // Cleanup function to prevent memory leaks
    return () => {
      mounted = false
      // Remove media query listener if it was initialized
      mediaQuery?.removeEventListener('change', handleChange)
    }
  }, [handleChange])  // Only handleChange dependency - device capabilities are stable for session

  useEffect(() => {
    const onInteractiveGlobeUnavailable = (): void => {
      setShouldShowInteractive(false)
    }
    window.addEventListener(INTERACTIVE_GLOBE_UNAVAILABLE_EVENT, onInteractiveGlobeUnavailable)
    return () => {
      window.removeEventListener(INTERACTIVE_GLOBE_UNAVAILABLE_EVENT, onInteractiveGlobeUnavailable)
    }
  }, [])

  return (
    // ErrorBoundary catches any rendering errors and shows static fallback
    <ErrorBoundary fallback={<StaticGlobe />}>
      <GlobalInsights 
        key={pathname} // CRITICAL REMOUNTING STRATEGY
        // 
        // WHY key={pathname} IS ESSENTIAL:
        // 
        // COMPONENT LIFECYCLE MANAGEMENT:
        // - Forces complete remount of GlobalInsights when user navigates
        // - Resets all internal state (animation, intersection observers, etc.)
        // - Prevents memory leaks from stale references
        // - Ensures clean slate for each page visit
        // 
        // INTERACTION WITH OTHER OPTIMIZATIONS:
        // - Works WITH removal of pathname from useEffect dependencies
        // - useEffect runs once per mount (not per navigation)
        // - Navigation triggers remount via key change, not effect re-run
        // - This separation is more performant and predictable
        // 
        // EDGE CASES HANDLED:
        // - Rapid navigation (each nav gets fresh component instance)
        // - Browser back/forward (pathname change triggers remount)
        // - Direct URL navigation (new mount with clean state)
        // - Memory leaks (old component fully unmounts and cleans up)
        // 
        // PERFORMANCE IMPLICATIONS:
        // - Remounting is slightly more expensive than state reset
        // - But prevents complex state management bugs
        // - Globe animation cleanup/restart is handled automatically
        // - Overall more reliable than manual state management
        shouldShowInteractive={shouldShowInteractive}
      />
    </ErrorBoundary>
  )
})